import { useCallback, useEffect, useRef, useState } from 'react'

import { useParams } from 'react-router-dom'

// Services
import { Loader } from 'components/Loader/Loader'
import ButtonTabNavbar from 'components/UI/TabNavbar/ButtonTabNavbar'
import { PostRenderer } from 'components-ref/organisms'
import { usePageTitle } from 'context/pageTitleContext'
import { usePreviewContext } from 'context/previewContext'
import { useFilterQuery, useInfiniteFeed } from 'helpers/hooks'
import { getRole } from 'helpers/util'
import WithoutHeaderSection from 'layouts/WithoutHeaderSection/WithoutHeaderSection'
import { getPlaylistFeed } from 'services/endpoints/media'
import { useUserProfile } from 'store'
import { type IPost } from 'types/interfaces/ITypes'

// Styling
import styles from './_mediaPlaylist.module.scss'

export const MediaPlaylists = () => {
    const filter = useFilterQuery()
    const search = filter.get('searchTerm')
    const modelId = filter.get('model_id')
    const type = filter.get('type') ?? 'all'
    const { setTitle } = usePageTitle()

    const { id: playlistId } = useParams<{ id: string }>()
    const userData = useUserProfile()
    const userId = userData?.id

    const { setInitialData, selectedPostData, setSelectedPostData } =
        usePreviewContext()

    const [, setAutocompleteTerm] = useState<string>(search ?? '')
    const [, setShouldAutocomplete] = useState(false)
    const [feedPosts, setFeedPosts] = useState<IPost[]>([])
    const isMessage = (post: IPost) =>
        post.type === 'message'
    const {
        data,
        error,
        isFetching,
        isFetchingNextPage,
        hasNextPage,
        status,
        fetchNextPage
    } = useInfiniteFeed(
        ['getPlaylistFeed', { filter: type, playlistId, model_id: modelId }],
        getPlaylistFeed
    )

    useEffect(() => {
        if (!search) {
            setShouldAutocomplete(true)
        }
    }, [search])

    useEffect(() => {
        const playlistTitle = (data?.pages?.[0]?.page?.data as any)?.meta?.playlist?.title

        setTitle(playlistTitle || 'Playlists')
    }, [data, setTitle])

    const setInitialDataRef = useRef(setInitialData)

    useEffect(() => {
        setInitialDataRef.current = setInitialData
    }, [setInitialData])

    useEffect(() => {
        if (!userId) return

        setInitialDataRef.current(userId, type ?? 'all', 'playlist')
    }, [userId, type])

    useEffect(() => {
        if (selectedPostData) {
            const selectedPost = feedPosts.find(
                post => post.id === selectedPostData.id
            )

            if (selectedPost) {
                setSelectedPostData(selectedPost)
            }
        }
    }, [feedPosts, selectedPostData, setSelectedPostData])

    useEffect(() => {
        if (data && !isFetching) {
            const posts = data.pages.flatMap(page => {
                return page.page.data.data
            })

            setFeedPosts(posts)
        }
    }, [data, isFetching])

    const observer = useRef<IntersectionObserver>()

    const lastPostRef = useCallback(
        (node: HTMLDivElement | null) => {
            if (isFetchingNextPage) return

            if (observer.current) {
                observer.current.disconnect()
            }

            observer.current = new IntersectionObserver(
                entries => {
                    if (entries[0].isIntersecting && hasNextPage) {
                        fetchNextPage()
                    }
                },
                { rootMargin: '1500px' }
            )

            if (node) {
                observer.current.observe(node)
            }
        },
        [isFetchingNextPage, hasNextPage, fetchNextPage]
    )

    if (status === 'error') {
        return <p>Error: {error?.message}</p>
    }

    return (
        <WithoutHeaderSection customClass='purchases'>
            <div className={styles.mediaplaylist}>
                <div className={styles.wrapper}>
                    <div className={styles.navigation}>
                        <ButtonTabNavbar
                            type='light'
                            navArr={['all', 'photos', 'videos']}
                            active={type}
                        />
                    </div>

                    <div className={styles.content}>
                        {status === 'loading' ? (
                            <div className={styles.loader}>
                                <Loader />
                            </div>
                        ) : (
                            <>
                                {feedPosts.map((post: IPost, index: number) => {
                                    const isLastPost =
                                        feedPosts.length === index + 1

                                    const postRenderer = (
                                        <PostRenderer
                                            key={post.id}
                                            postIndex={index}
                                            role={getRole(userData!, post)}
                                            postId={post.id}
                                            postData={post}
                                            hideComments={
                                                isMessage(
                                                    post
                                                )
                                            }
                                            dataQuery={[
                                                'getPlaylistFeed',
                                                {
                                                    filter: type,
                                                    user_id: modelId,
                                                    playlistId
                                                }
                                            ]}
                                        />
                                    )

                                    if (isLastPost) {
                                        return (
                                            <div
                                                key={post.id}
                                                ref={lastPostRef}
                                            >
                                                {postRenderer}
                                            </div>
                                        )
                                    }

                                    return postRenderer
                                })}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </WithoutHeaderSection>
    )
}